import { MoreVert } from '@mui/icons-material';
import { Flex, Text } from '@legacy/components/ui-components';
import { ReactNode, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import postResyncDataOnAllocation from '@legacy/apis/resourcePlannerAPI/post/postResyncDataOnAllocation';
import { Menu, MenuItem } from '@mui/material';
import {
  Dialog,
  DialogTitle,
  IconButton,
} from '@legacy/components/mui-components';
import { DistributeUnplannedWorkloadModalContent } from '../DistributeUnplannedWorkloadModalContent';

interface IUnplannedCell {
  children: ReactNode;
  resourceId?: string;
  workItemId?: string;
  hierarchyType: string;
  resourceName: string;
  workItemName?: string;
  isLowestLevel: boolean;
  startsAt?: string;
  endsAt?: string;
  negativeUnplannedHours: number;
  type?: string;
  hasChildWithEndDateOnTodayOrAfter?: boolean;
  workItemSourceReferenceId?: string;
  workItemType?: string;
  resourceSourceReferenceId?: string;
}

const UnplannedCell = ({
  children,
  resourceId,
  workItemId,
  hierarchyType,
  resourceName,
  workItemName,
  isLowestLevel,
  startsAt,
  endsAt,
  negativeUnplannedHours,
  type,
  hasChildWithEndDateOnTodayOrAfter,
  workItemSourceReferenceId,
  workItemType,
  resourceSourceReferenceId,
}: IUnplannedCell) => {
  const { t } = useTranslation('resourcePlanner');
  const [
    distributeUnplannedWorkloadModalIsOpen,
    setDistributeUnplannedWorkloadModalIsOpen,
  ] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const isResourceRow = hierarchyType.toLowerCase() === 'resource';

  const rowType = type?.toLowerCase();

  const { mutate: postResyncData } = postResyncDataOnAllocation();

  const headingText = isResourceRow ? (
    <Trans
      i18nKey="ModalDistributeUnplannedResourceHeader"
      key="ModalDistributeUnplannedResourceHeader"
      defaults={t('ModalDistributeUnplannedResourceHeader')}
      values={{ resourceName }}
      components={[
        <Text semibold inheritSize as="span">
          {resourceName}
        </Text>,
      ]}
    />
  ) : (
    <Trans
      i18nKey="ModalDistributeUnplannedResourceAndWorkItemHeader"
      key="ModalDistributeUnplannedResourceAndWorkItemHeader"
      defaults={t('ModalDistributeUnplannedResourceAndWorkItemHeader')}
      values={{ resourceName, workItemName }}
      components={[
        <Text semibold inheritSize as="span">
          {resourceName}
        </Text>,
        <Text semibold inheritSize as="span">
          {workItemName}
        </Text>,
      ]}
    />
  );

  return (
    <Flex gap="xxSmall" horizontalAlignment="justify">
      <IconButton
        id={`DistributeUnplannedButton-${resourceId}-${workItemId}`}
        aria-controls={`DistributeUnplannedMenu-${resourceId}-${workItemId}`}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : 'false'}
        onClick={handleClick}
        data-automation-id="unplanned-menu"
        title="Show more"
        size="small"
      >
        <MoreVert fontSize="small" />
      </IconButton>
      <Menu
        id={`DistributeUnplannedMenu-${resourceId}-${workItemId}`}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        slotProps={{
          list: {
            'aria-labelledby': `DistributeUnplannedButton-${resourceId}-${workItemId}`,
          },
        }}
        disableScrollLock
      >
        <MenuItem
          onClick={() => setDistributeUnplannedWorkloadModalIsOpen(true)}
        >
          {t('ContextMenuDistributeUnplannedWork')}
        </MenuItem>
        <Dialog
          open={distributeUnplannedWorkloadModalIsOpen}
          onClose={() => setDistributeUnplannedWorkloadModalIsOpen(false)}
          scroll={!isResourceRow ? 'paper' : 'body'}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle
            closeText={t('Close')}
            headingComponent={
              <Text semibold inheritSize>
                {headingText}
              </Text>
            }
            closeModal={() => setDistributeUnplannedWorkloadModalIsOpen(false)}
          />
          <DistributeUnplannedWorkloadModalContent
            isResourceRow={isResourceRow}
            resourceId={resourceId}
            workItemId={workItemId}
            isLowestLevel={isLowestLevel}
            startsAt={startsAt}
            endsAt={endsAt}
            negativeUnplannedHours={negativeUnplannedHours}
            rowType={rowType}
            hasChildWithEndDateOnTodayOrAfter={
              hasChildWithEndDateOnTodayOrAfter
            }
            handleClose={() => setDistributeUnplannedWorkloadModalIsOpen(false)}
          />
        </Dialog>

        <MenuItem
          onClick={() => {
            if (
              workItemSourceReferenceId &&
              workItemType &&
              resourceSourceReferenceId
            ) {
              postResyncData({
                workItemSourceReferenceId,
                workItemType,
                resourceSourceReferenceId,
              });
            }
          }}
          disabled={
            !workItemSourceReferenceId ||
            !workItemType ||
            workItemType === 'Unknown' ||
            !resourceSourceReferenceId
          }
        >
          {t('ContextMenuResyncProjectPlan')}
        </MenuItem>
      </Menu>
      <span>{children}</span>
    </Flex>
  );
};

export default UnplannedCell;
